import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

/** Prevents a crashed AI response from taking down the whole storefront. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('Storefront error boundary caught:', error, info);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-surface p-6 text-center">
          <div className="text-2xl">😵‍💫</div>
          <p className="text-sm font-medium text-primary">Something went wrong.</p>
          <p className="max-w-xs text-xs text-mid">
            The chat hit an unexpected error. You can try reloading the conversation.
          </p>
          <button
            onClick={this.handleReset}
            className="mt-2 rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white transition-transform active:scale-95"
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
